import React, { Component } from 'react';
import Modal from 'components/shared/Modal/Modal';
import Dropdown from 'components/shared/Dropdown/Dropdown';
import SearchBar from 'components/shared/SearchBar/SearchBar';
import ScrollReactTable from 'components/shared/ScrollReactTable/ScrollReactTable';
import { Link } from 'react-router-dom';
import SvgAF, { IconAndText } from 'components/shared/SvgAF/SvgAF';
import moment from 'moment';
import { isObjectEmpty, reactTableSortOnClick } from 'common/utils/helpers';
import { Status, deviceDisplayTypes, SWUpdateStatuses } from 'common/utils/constants';
import { updateSoftwareUpdateStatuses, setDeviceDisplayMode } from 'redux/actions';
import { connect, useDispatch } from 'react-redux';
import { IconLoader } from 'common/IconLoader';
import { createStatusSelector, selectStoredBundleDictionary, selectActiveDeviceDisplayMode } from 'redux/selectors';
import SWUpdateStatusChart from './SWUpdateStatusChart';
import styles from './ActionDetailsModal.module.css';

const columns = (parent) => [
  {
    Header: (col) => {
      let displayType = parent.props.deviceDisplayMode;
      let dispatch = useDispatch();
      let deviceColHeader = 'Device ID';

      switch (displayType) {
        case deviceDisplayTypes.ID:
          deviceColHeader = 'Device ID';
          break;
        case deviceDisplayTypes.NAME:
          deviceColHeader = 'Device Name';
          break;
      }

      return (
        <div className={styles.ActionDetailsTable_Header_OuterDiv}>
          <div className={styles.idNameSwitcher}>
            <SvgAF
              img={displayType === deviceDisplayTypes.ID ? IconLoader.icon_toggle_id : IconLoader.icon_toggle_name}
              hover={
                displayType === deviceDisplayTypes.ID
                  ? IconLoader.icon_toggle_id_hover
                  : IconLoader.icon_toggle_name_hover
              }
              tooltip={displayType === deviceDisplayTypes.ID ? 'Toggle to Name' : 'Toggle to ID'}
              onClick={() => {
                dispatch(
                  setDeviceDisplayMode(
                    displayType === deviceDisplayTypes.ID ? deviceDisplayTypes.NAME : deviceDisplayTypes.ID
                  )
                );
                parent.setState({ deviceDisplayMode: displayType });
              }}
            />
          </div>

          <IconAndText
            img={IconLoader.icon_table_sort}
            hoverImg={IconLoader.icon_table_sort_hover}
            text={deviceColHeader}
            reverse
            onClick={() => reactTableSortOnClick(parent, 'id_col_id')}
          />
        </div>
      );
    },
    id: 'id_col_id',
    minWidth: 200,
    width: 196,
    accessor: (device) => {
      let deviceObj = parent.props?.deviceDictionary?.[device.device_id];
      let defaultDeviceAccessor = device.device_id ? device.device_id : '';

      switch (parent.props.deviceDisplayMode) {
        case deviceDisplayTypes.ID:
          return defaultDeviceAccessor;
        case deviceDisplayTypes.NAME:
          return deviceObj?.userLabel ? deviceObj.userLabel : defaultDeviceAccessor;
        default:
          return defaultDeviceAccessor;
      }
    },

    Cell: (row) => {
      let device = row.original;
      const isGa2 = parent.props?.action?.input?.isGa2;
      const url = isGa2
        ? `/console/devices/pivot?deviceId=${device.device_id}`
        : `/console/devices/detail?${device.device_id}`;
      return (
        <div className={styles.idColumn} data-tip={row.value}>
          <Link to={url}>{row.value}</Link>
        </div>
      );
    }
  },

  {
    Header: (col) => {
      return (
        <div className={styles.ActionDetailsTable_Header_OuterDiv}>
          <IconAndText
            img={IconLoader.icon_table_sort}
            hoverImg={IconLoader.icon_table_sort_hover}
            text={'Progress'}
            reverse
            onClick={() => reactTableSortOnClick(parent, 'actions_details_col_id')}
          />
        </div>
      );
    },
    id: 'actions_details_col_id',
    accessor: (device) => {
      if (device && device.upgrade_status) {
        if (device.upgrade_status.status === 'downloading') {
          return (
            <div
              className={styles.ActionDetailsModal_Table_ProgressBarOuterDiv}
              data-tip={device.upgrade_status.message}
            >
              <div>Downloading</div>
              <div className={styles.ProgressBar}>
                <div className={styles.BarSpace} />
                <div className={styles.BarFilled} style={{ width: device.upgrade_status.status_info.progress + '%' }} />
              </div>
              {device.upgrade_status.status_info && (
                <div data-tip={device.upgrade_status?.message}>{device.upgrade_status.status_info.progress + '%'}</div>
              )}
            </div>
          );
        } else {
          return <div data-tip={device.upgrade_status?.message}>{device.upgrade_status.status}</div>;
        }
      } else {
        return 'unknown';
      }
    },
    Cell: (row) => {
      return (
        <div className={styles.ActionsDetailsTable_ProgressDetailsCell}>
          {row.value === 'completed' && (
            <div className={styles.ActionsDetailsTable_ProgressDetailsCell_Text_Completed}>{row.value}</div>
          )}
          {row.value === 'error' && (
            <div className={styles.ActionsDetailsTable_ProgressDetailsCell_Text_Error}>{row.value}</div>
          )}
          {!(row.value === 'completed' || row.value === 'error') && (
            <div className={styles.ActionsDetailsTable_ProgressDetailsCell_Text}>{row.value}</div>
          )}
        </div>
      );
    }
  }
];

class ActionDetailsModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      dataPollTimer: null,
      ddFilter: 'all devices',
      searchString: '',
      deviceDisplayMode: deviceDisplayTypes.ID,
      deviceNamesReady: false,
      deviceCount: 0,
      totalDevicesLength: 0,
      activeErrorCount: 0, //if api call fails once we can ignore, repeated fails and we'll use this to display error overlay
      activeFirstFetch: true //whether first fetch is still in progress for an active table. Used to display spinner
    };

    this.getStatusData = this.getStatusData.bind(this);
    this.filterDevices = this.filterDevices.bind(this);
    this.columns = columns(this);
  }

  pollData() {
    const { swUpdateStatus } = this.props;
    if (swUpdateStatus && swUpdateStatus === Status.FAILURE) {
      this.setState({ activeErrorCount: this.state.activeErrorCount + 1 });
    } else if (swUpdateStatus && swUpdateStatus === Status.SUCCESS && this.state.activeErrorCount !== 0) {
      this.setState({ activeErrorCount: 0 });
    }

    if (swUpdateStatus !== Status.PENDING) {
      this.getStatusData();
    }

    this.dataPollTimer = setTimeout(() => {
      this.pollData();
    }, 6000);
  }

  getStatusData() {
    this.props.dispatch(updateSoftwareUpdateStatuses(this.props.action.input.monitor_url));
  }

  setDropdownFilter(selection) {
    this.setState({ ddFilter: selection.toLowerCase() });
  }

  filterDevices(devices) {
    //console.log("unfiltered deviceList:", devices);
    return devices.filter(
      (device) =>
        device.upgrade_status != null &&
        device.device_id.includes(this.state.searchString) &&
        (this.state.ddFilter === 'all devices' || device.upgrade_status.status === this.state.ddFilter)
    );
  }

  componentDidMount() {
    if (this.props.actionTableType === 'active') {
      this.pollData();
    } else if (this.props.actionTableType === 'past') {
      this.getStatusData();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.dataPollTimer);
  }

  componentDidUpdate() {
    const { swUpdateStatus } = this.props;
    if (
      this.state.activeFirstFetch &&
      (swUpdateStatus === Status.SUCCESS || (swUpdateStatus === Status.FAILURE && this.state.activeErrorCount > 1))
    ) {
      //if we were doing first fetch, we know it returned now
      this.setState({ activeFirstFetch: false });
    }
    if (this.state.activeErrorCount > 0 && swUpdateStatus === Status.SUCCESS) {
      this.setState({ activeErrorCount: 0 });
    }
  }

  render() {
    const { swUpdateStatus } = this.props;
    if (this.props.action == null) {
      return null;
    }

    let pageSize = 0;
    let filteredDevices = [];

    if (this.props.action.devices != null) {
      //console.log("props.action.devices:", this.props.action.devices);
      let deviceList = Object.keys(this.props.action.devices).map((key) => this.props.action.devices[key]);

      filteredDevices = this.filterDevices(deviceList);
      pageSize = filteredDevices.length;
    }
    let dropdownOptions = Object.values(SWUpdateStatuses);
    dropdownOptions.unshift('all devices');

    let isFetching =
      (this.props.actionTableType === 'past' && swUpdateStatus === Status.PENDING) ||
      (this.props.actionTableType === 'active' && this.state.activeFirstFetch) ||
      !(this.props.action && this.props.action.devices && !isObjectEmpty(this.props.action.devices));

    let overlay = null;
    if (
      (this.props.actionTableType === 'past' && swUpdateStatus === Status.FAILURE) ||
      this.state.activeErrorCount > 1
    ) {
      overlay = !isFetching && (
        <div className="Global_Table_ErrorOverlay">
          <div className="Global_Table_Error_Div">
            <div className="Global_Table_ErrorOverlay_Title">Error </div>
            <div className="Global_Table_ErrorOverlay_Message">
              An unexpected problem occurred and this data is unavailable. Please try refreshing the page, and if the
              problem persists, contact us at
              <a href="mailto:support@pivotalcommware.com"> support@pivotalcommware.com</a>
            </div>
          </div>
        </div>
      );
    }

    let author = null;
    //TODO determine author of action

    return (
      <div className={styles.actionDetailsModalContainer}>
        <Modal
          width={1160}
          height={800}
          close={() => {
            this.props.onClose ? this.props.onClose() : this.props.setDetailsModalOpen(false);
          }}
        >
          <div className={styles.actionDetailsModalContent}>
            {this.props.actionType === 'BulkSoftwareUpdate' && (
              <div className={styles.titleContainer}>
                <span>Software update to </span>
                {/* <span className={styles.vLabel}>v </span>
              <span className={styles.versionLabel}>{"x.x.x"}</span>
              <span> to </span> */}
                <span className={styles.vLabel}>v </span>
                <span className={styles.versionLabel}>{this.props.action.input.swVersion}</span>
              </div>
            )}

            {this.props.actionType === 'BulkSoftwareUpdate' && (
              <div className={styles.descriptionContainer}>
                <div className={styles.timeSection}>
                  <span>
                    {this.props.action.input.schedule === 'now'
                      ? 'Download started without a schedule time'
                      : `Download scheduled for ${moment(this.props.action.input.schedule).format('lll')}`}
                  </span>
                </div>
                <div className={styles.timeSection}>
                  <span>
                    {this.props.action.input?.activationDate === undefined
                      ? 'Activation scheduled upon completion of the download'
                      : `Activation scheduled for ${moment(this.props.action.input.activationDate).format('lll')}`}
                  </span>
                </div>
                {author != null ? (
                  <div className={styles.authorSection}>
                    <span>Performed by </span>
                    <Link className={styles.authorName} to={'/console/user/' + author}>
                      author
                    </Link>{' '}
                    {/* This is just a placeholder, the real urls might be different */}
                  </div>
                ) : null}
                <span></span>
              </div>
            )}

            <div className={styles.contentContainer}>
              <div className={styles.tableSide}>
                <div className={styles.tableSideTitle}> List of devices: </div>
                <div className={styles.deviceFilterBox}>
                  <div className={styles.searchBarContainer}>
                    <SearchBar searchQuery={(inputString) => this.setState({ searchString: inputString })} />
                  </div>
                  <div className={styles.dropdownContainer}>
                    <Dropdown
                      colorMode={'white'}
                      selectItem={(selection) => this.setDropdownFilter(selection)}
                      title={this.state.ddFilter ? this.state.ddFilter : 'All devices'}
                      data={dropdownOptions}
                    />
                  </div>
                  <div className={styles.deviceCounterContainer}>
                    <span>Showing </span>
                    <span className={styles.deviceCount}>{filteredDevices.length}</span>
                    <span> total devices</span>
                  </div>
                  <div className={styles.detailsTableContainer}>
                    <ScrollReactTable
                      showPagination={false}
                      resizable={false}
                      sortable={false}
                      fetching={isFetching}
                      overlay={overlay}
                      minRows={11}
                      className={styles.ActionsDetailTable + ' Global_Table'}
                      defaultPageSize={0}
                      pageSize={pageSize}
                      getTdProps={(state, rowInfo, column, instance) => {
                        return {
                          onClick: (e, handleOriginal) => {
                            if (handleOriginal) {
                              handleOriginal(); /* Suggested by documentation, makes sure that default click functions still get called if necessary*/
                            }
                          }
                        };
                      }}
                      getTrProps={(state, rowInfo, column) => {
                        if (rowInfo) {
                          /* This will assign the class 'selected-row' to a row when it has been clicked on. This class is used to apply css style to the selected row*/
                          return {
                            className: 'rowStyle'
                          };
                        } else {
                          return {};
                        }
                      }}
                      data={filteredDevices}
                      columns={this.columns}
                      sorted={this.state.sorted}
                      onSortedChange={(newSorted, column, shiftKey) => {
                        this.setState({ sorted: this.state.sorted });
                        //do nothing
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.chartSide}>
                <SWUpdateStatusChart devices={filteredDevices} />
              </div>
            </div>
          </div>
        </Modal>
      </div>
    );
  }
}

export default connect((state, ownProps) => {
  const { action } = ownProps;
  const selectSWUpdateStatus = createStatusSelector(`SoftwareUpdateStatus-${action?.input.monitor_url}`);

  return {
    swUpdateStatus: selectSWUpdateStatus(state),
    deviceDisplayMode: selectActiveDeviceDisplayMode(state),
    deviceDictionary: selectStoredBundleDictionary(state)
  };
})(ActionDetailsModal);
